import React, { useState } from 'react'
import {
    useErrorStore,
    useLoadingStore,
    useCCExpireStore,
    useNavStore,
} from '../../state/store'
import RefreshFunctions from '../RefreshFunctions/RefreshFunctions'
import { IoIosCloseCircle } from 'react-icons/io'
import {
    HiMenu,
    HiBookOpen,
    HiLogout,
    HiChevronUp,
    HiChevronDown,
    HiX,
} from 'react-icons/hi'
import { MdEmail } from 'react-icons/md'
import { BsClipboard2DataFill } from 'react-icons/bs'
import { RiDiscordFill } from 'react-icons/ri'
import { HiCurrencyDollar } from 'react-icons/hi2'
import { NavLink } from 'react-router-dom'
import { useAuthStore } from '../../state/store'
import {
    StyledHeading,
    StyledWhite,
    StyledGreen,
    StyledExpiringContainer,
    StyledCloseButton,
    StyledLeftSection,
    StyledLogoGroup,
    StyledRightSection,
    StyledHamburgerButton,
    StyledMenuOverlay,
    StyledHamburgerMenu,
    StyledMenuHeader,
    StyledMenuCloseButton,
    StyledHamburgerLink,
    StyledHamburgerLinkIcon,
    StyledCollapseButton,
} from './styles'
import { AmplitudeEvents } from '../../hooks/amplitude'
import { useAmplitudeTrack } from '../../hooks/amplitude'
import { shouldRefreshWiseOptionsFormula } from '../../utils/wiseoptions-formula'
import { useClientConfig } from '../../hooks/useClientConfig'
import { BrokerageNavItem } from '../Nav/Nav'

const PageHeader = React.memo(() => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const { setSubscriptionCCStatus, status } = useCCExpireStore(
        (state) => state
    )
    const { setLoading } = useLoadingStore((state) => state)
    const { setError } = useErrorStore((state) => state)
    const trackEvent = useAmplitudeTrack()
    const { logout } = useAuthStore((state) => state)
    const { branding, isWisesheets } = useClientConfig()
    const { isCollapsed, toggleCollapsed } = useNavStore((state) => state)
    const pressLogout = () => {
        window.localStorage.removeItem('token')
        logout()
    }

    const reevaluateLiveData = async (isAllSheets: boolean) => {
        setLoading({ isLoading: true, message: 'Updating data...' })
        trackEvent(AmplitudeEvents.LIVE_DATA_UPDATED)
        try {
            await Excel.run(async (context) => {
                if (isAllSheets) {
                    const sheets = context.workbook.worksheets
                    sheets.load('items/name')
                    await context.sync()

                    for (const sheet of sheets.items) {
                        const foundRanges = sheet.findAllOrNullObject(
                            isWisesheets ? 'WISEPRICE' : 'FMPPRICE',
                            { completeMatch: false, matchCase: false }
                        )
                        foundRanges.calculate()

                        const foundRangesTTM = sheet.findAllOrNullObject(
                            'TTM',
                            {
                                completeMatch: false,
                                matchCase: false,
                            }
                        )
                        foundRangesTTM.calculate()

                        const foundRangesFunds = sheet.findAllOrNullObject(
                            isWisesheets ? 'WISEFUNDS' : 'FMPFUNDS',
                            { completeMatch: false, matchCase: false }
                        )
                        foundRangesFunds.calculate()

                        if (isWisesheets) {
                            const foundRangesOptions =
                                sheet.findAllOrNullObject('WISEOPTIONS', {
                                    completeMatch: false,
                                    matchCase: false,
                                })
                            foundRangesOptions.areas.load('items/formulas')
                            await context.sync()

                            if (foundRangesOptions.areas.items) {
                                const rangesToCalculate =
                                    foundRangesOptions.areas.items.filter(
                                        (range) => {
                                            const formula =
                                                range.formulas[0]?.[0]
                                            return (
                                                typeof formula === 'string' &&
                                                shouldRefreshWiseOptionsFormula(
                                                    formula
                                                )
                                            )
                                        }
                                    )
                                if (rangesToCalculate.length > 0) {
                                    rangesToCalculate.forEach((range) =>
                                        range.calculate()
                                    )
                                }
                            }
                        }
                    }
                } else {
                    const currentSheet =
                        context.workbook.worksheets.getActiveWorksheet()
                    const foundRanges = currentSheet.findAllOrNullObject(
                        isWisesheets ? 'WISEPRICE' : 'FMPPRICE',
                        { completeMatch: false, matchCase: false }
                    )
                    foundRanges.calculate()

                    const foundRangesTTM = currentSheet.findAllOrNullObject(
                        'TTM',
                        { completeMatch: false, matchCase: false }
                    )
                    foundRangesTTM.calculate()

                    const foundRangesFunds = currentSheet.findAllOrNullObject(
                        isWisesheets ? 'WISEFUNDS' : 'FMPFUNDS',
                        { completeMatch: false, matchCase: false }
                    )
                    foundRangesFunds.calculate()

                    if (isWisesheets) {
                        const foundRangesOptions =
                            currentSheet.findAllOrNullObject('WISEOPTIONS', {
                                completeMatch: false,
                                matchCase: false,
                            })
                        foundRangesOptions.areas.load('items/formulas')
                        await context.sync()

                        if (foundRangesOptions.areas.items) {
                            const rangesToCalculate =
                                foundRangesOptions.areas.items.filter(
                                    (range) => {
                                        const formula = range.formulas[0]?.[0]
                                        return (
                                            typeof formula === 'string' &&
                                            shouldRefreshWiseOptionsFormula(
                                                formula
                                            )
                                        )
                                    }
                                )
                            if (rangesToCalculate.length > 0) {
                                rangesToCalculate.forEach((range) =>
                                    range.calculate()
                                )
                            }
                        }
                    }
                }
                await context.sync()
            })
        } catch {
            setError({
                hasError: true,
                errorMessage: 'Something went wrong. Please try again.',
            })
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    const refreshErrors = async () => {
        setLoading({ isLoading: true, message: 'Refreshing errors...' })
        trackEvent(AmplitudeEvents.REFRESH_ERRORS)
        try {
            await Excel.run(async (context) => {
                var currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()

                const foundRangesXlduf = currentSheet.findAllOrNullObject(
                    '_xlduf_',
                    { completeMatch: false, matchCase: false }
                )

                if (foundRangesXlduf.areas) {
                    foundRangesXlduf.areas.load('items')
                    await context.sync()

                    if (foundRangesXlduf.areas.items) {
                        foundRangesXlduf.areas.items.forEach((range) => {
                            const formula = range.formulas[0][0] as string
                            if (formula) {
                                const newFormula = formula.replace(
                                    /(=).*?_xlduf_/g,
                                    '$1'
                                )
                                range.formulas = [[newFormula]]
                            }
                        })
                    }
                }

                var foundRanges = currentSheet.findAllOrNullObject(
                    'Too many simultaneous requests.',
                    { completeMatch: false, matchCase: false }
                )
                foundRanges.calculate()

                var foundRanges2 = currentSheet.findAllOrNullObject('#VALUE!', {
                    completeMatch: false,
                    matchCase: false,
                })
                foundRanges2.calculate()

                var foundRanges3 = currentSheet.findAllOrNullObject(
                    'Unexpected result. Verify your function call and retry.',
                    { completeMatch: false, matchCase: false }
                )
                foundRanges3.calculate()

                var foundRanges4 = currentSheet.findAllOrNullObject('#NAME?', {
                    completeMatch: false,
                    matchCase: false,
                })
                foundRanges4.calculate()

                var foundRanges5 = currentSheet.findAllOrNullObject(
                    'Unexpected error. Try function with multiple tickers.',
                    { completeMatch: false, matchCase: false }
                )
                foundRanges5.calculate()

                var foundRanges6 = currentSheet.findAllOrNullObject(
                    'Please login to use the WISE/WISEPRICE function.',
                    {
                        completeMatch: false, // find will match the whole cell value
                        matchCase: false, // find will not match case
                    }
                )
                foundRanges6.calculate()

                await context.sync()
            })
        } catch {
            setError({
                hasError: true,
                errorMessage: 'Something went wrong. Please try again.',
            })
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    return (
        <>
            <StyledHeading>
                <StyledLeftSection>
                    <StyledLogoGroup
                        onClick={() => setIsMenuOpen(true)}
                        style={{ cursor: 'pointer' }}
                    >
                        <StyledHamburgerButton aria-label="Menu">
                            <HiMenu />
                        </StyledHamburgerButton>
                        {!isWisesheets ? (
                            <StyledWhite>{branding.companyName}</StyledWhite>
                        ) : (
                            <StyledWhite>
                                <StyledGreen>Wise</StyledGreen>sheets
                            </StyledWhite>
                        )}
                    </StyledLogoGroup>
                </StyledLeftSection>

                <StyledRightSection>
                    <RefreshFunctions
                        refreshFunctions={reevaluateLiveData}
                        refreshErrors={refreshErrors}
                    />
                    {isWisesheets && <BrokerageNavItem />}
                    <StyledCollapseButton
                        onClick={toggleCollapsed}
                        aria-label={isCollapsed ? 'Expand nav' : 'Collapse nav'}
                    >
                        {isCollapsed ? <HiChevronDown /> : <HiChevronUp />}
                    </StyledCollapseButton>
                </StyledRightSection>
            </StyledHeading>

            {/* Slide-in side menu overlay */}
            <StyledMenuOverlay
                $isOpen={isMenuOpen}
                onClick={() => setIsMenuOpen(false)}
            />
            <StyledHamburgerMenu $isOpen={isMenuOpen}>
                <StyledMenuHeader>
                    {!isWisesheets ? (
                        <StyledWhite>{branding.companyName}</StyledWhite>
                    ) : (
                        <StyledWhite>
                            <StyledGreen>Wise</StyledGreen>sheets
                        </StyledWhite>
                    )}
                    <StyledMenuCloseButton
                        onClick={() => setIsMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <HiX />
                    </StyledMenuCloseButton>
                </StyledMenuHeader>
                <StyledHamburgerLink
                    target="_blank"
                    href={branding.docsLink}
                    onClick={() => setIsMenuOpen(false)}
                >
                    <StyledHamburgerLinkIcon as={HiBookOpen} />
                    Documentation
                </StyledHamburgerLink>
                <StyledHamburgerLink
                    target="_blank"
                    href={branding.availableDataLink}
                    onClick={() => setIsMenuOpen(false)}
                >
                    <StyledHamburgerLinkIcon as={BsClipboard2DataFill} />
                    Available Data
                </StyledHamburgerLink>
                <StyledHamburgerLink
                    target="_blank"
                    href={`mailto:${branding.contactLink}`}
                    onClick={() => setIsMenuOpen(false)}
                >
                    <StyledHamburgerLinkIcon as={MdEmail} />
                    Contact
                </StyledHamburgerLink>
                {isWisesheets && (
                    <>
                        <StyledHamburgerLink
                            target="_blank"
                            href="https://discord.com/invite/z8PgPqhCxs"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <StyledHamburgerLinkIcon as={RiDiscordFill} />
                            Discord
                        </StyledHamburgerLink>
                        <StyledHamburgerLink
                            target="_blank"
                            href="https://wisesheets.io/referral"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <StyledHamburgerLinkIcon as={HiCurrencyDollar} />
                            Refer and earn
                        </StyledHamburgerLink>
                    </>
                )}
                <StyledHamburgerLink
                    as={NavLink}
                    to="/"
                    onClick={() => {
                        setIsMenuOpen(false)
                        pressLogout()
                    }}
                >
                    <StyledHamburgerLinkIcon as={HiLogout} />
                    Logout
                </StyledHamburgerLink>
            </StyledHamburgerMenu>

            {status.expiring ? (
                <StyledExpiringContainer>
                    <div>⚠️ Your plan is expiring soon</div>
                    <a
                        href="https://wisesheets.io/manage-account"
                        target="_blank"
                    >
                        Update billing
                    </a>
                    <StyledCloseButton
                        onClick={() => setSubscriptionCCStatus(false)}
                    >
                        <IoIosCloseCircle size={24} />
                    </StyledCloseButton>
                </StyledExpiringContainer>
            ) : null}
        </>
    )
})

PageHeader.displayName = 'PageHeader'

export default PageHeader
